import { computed, ref } from 'vue';
import { defineStore, storeToRefs } from 'pinia';
import useAuthorize from '@/common/auth/useAuthorize';
import { FRAME_NAMES } from '@/common/define/apiTrace.define';
import { useMyInfoStore } from '@/config/stores/my-info';
import type {
  AddedKeyword,
  FilteredKeyword,
  Keyword,
} from '@/common/components/molecules/keywordSettingModal/keywordSettingModal.types';
import { fixColor } from '@/common/components/molecules/keywordSettingModal/keywordSettingModal.utils';
import { isEqual } from 'lodash-es';
import {
  getV7MetaLogKeyword,
  getV7MetaLogKeywordCustomByUserId,
  patchV7MetaLogKeyword,
  patchV7MetaLogKeywordCustom,
} from '@/api-client/metav6/queries/keyword-controller/keyword-controller';

export const useLogsKeywordSettingStore = defineStore('keywordSetting', () => {
  const { myInfo } = storeToRefs(useMyInfoStore());
  const userId = computed(() => myInfo.value?.userId);

  const configKeywordList = ref<FilteredKeyword[]>([]);
  const userKeywordList = ref<AddedKeyword[]>([]);

  const fetchConfigKeywordList = async () => {
    if (!userId.value || userId.value <= 0) return;

    try {
      const {
        data: { data = [] },
      } = await getV7MetaLogKeyword({
        frameName: FRAME_NAMES.LOG_LOGGING.LOGGING_KEYWORD,
      });

      configKeywordList.value =
        data
          .map<FilteredKeyword>((item) => ({
            type: item.type === 'regexp' ? 'regexp' : 'string',
            keyword: item.keyword ?? '',
            lineColor: fixColor('line', item.lineColor),
            bgColor: fixColor('bg', item.bgColor),
          }))
          .toSorted((a, b) => a.keyword.localeCompare(b.keyword)) ?? [];
    } catch {
      configKeywordList.value = [];
    }
  };

  const fetchUserKeywordList = async () => {
    if (!userId.value || userId.value <= 0) return;

    try {
      const {
        data: { data = [] },
      } = await getV7MetaLogKeywordCustomByUserId(
        userId.value,
        {},
        {
          frameName: FRAME_NAMES.LOG_LOGGING.LOGGING_CUSTOM_KEYWORD,
        },
      );
      userKeywordList.value = data
        .map((item) => ({
          id: item.customKeywordId,
          type: 'string' as const,
          keyword: item.keyword ?? '',
          bgColor: fixColor('bg', item.bgColor),
        }))
        .toSorted((a, b) => a.keyword.localeCompare(b.keyword));
    } catch {
      userKeywordList.value = [];
    }
  };

  const updateConfigKeywordList = async (list: FilteredKeyword[]) => {
    if (isEqual(configKeywordList.value, list)) {
      return;
    }

    try {
      await patchV7MetaLogKeyword(list, {
        frameName: FRAME_NAMES.LOG_LOGGING.LOGGING_KEYWORD_UPDATE,
      });
      configKeywordList.value = list;
    } catch {
      throw new Error();
    }
  };

  const updateUserKeywordList = async (list: AddedKeyword[]) => {
    if (isEqual(userKeywordList.value, list)) {
      return;
    }

    try {
      await patchV7MetaLogKeywordCustom(
        list.map((item) => ({
          userId: userId.value,
          keywordId: item.id as number | undefined,
          keyword: item.keyword,
          bgColor: item.bgColor,
        })),
        {},
        {
          frameName: FRAME_NAMES.LOG_LOGGING.LOGGING_CUSTOM_KEYWORD_UPDATE,
        },
      );
      userKeywordList.value = list.toSorted((a, b) => a.keyword.localeCompare(b.keyword));
    } catch {
      throw new Error();
    }
  };

  const hasKeywordEditAuth = computed(() => useAuthorize('logging', 'logKeyword'));

  const highlightInfo = computed<Keyword[]>(() => [
    ...configKeywordList.value,
    ...userKeywordList.value,
  ]);

  return {
    fetchConfigKeywordList,
    fetchUserKeywordList,
    configKeywordList,
    userKeywordList,

    hasKeywordEditAuth,
    highlightInfo,

    updateConfigKeywordList,
    updateUserKeywordList,
  };
});
